import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../Client'

/**
 * @typedef {object} CodeOutcome
 * @property {string} Code - Interventiecode uit de meetstandaard
 * @property {string} Name
 * @property {number} Units
 * @property {number} Co2Kg
 * @property {number} PrivateEur
 * @property {number} HouseholdCount
 *
 * @typedef {object} Outcomes
 * @property {number|null} Co2Kg
 * @property {number|null} PrivateEur
 * @property {number} HouseholdCount
 * @property {number} UnknownCount
 * @property {Array<CodeOutcome>} PerCode
 *
 * @typedef {object} MaterialOutcomesBreakdown
 * @property {Array<{CompagnyID: string, Name: string, Outcomes: Outcomes}>} projects - Grootste CO2 eerst
 * @property {Array<string>} pending - Namen van projecten zonder berekening
 * @property {Outcomes|null} total - Gelijk aan het getal op de tegel
 * @property {number|null} computedAt - Oudste rekenmoment in ms
 */

/**
 * De besparingen van materialen per project en per maatregel — de
 * detailpagina achter de tegels 'Bespaarde CO2' en 'Bespaard op
 * energierekening'. Via een Cloud Function: een portfolio mag de collecties
 * van zijn projecten niet lezen, en een geweigerde read is in de browser een
 * lege lijst in plaats van een fout.
 * AI-CONTEXT: See docs/features/materiaal-uitkomsten-tegels.md
 *
 * `data` is null zolang er geen antwoord is — iets anders dan een antwoord
 * zonder projecten.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @returns {{data: MaterialOutcomesBreakdown|null, loading: boolean, error: Error|null}}
 */
const useMaterialOutcomesBreakdown = () => {
    const [data, setData] = useState(/** @type {MaterialOutcomesBreakdown|null} */ (null))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(/** @type {Error|null} */ (null))

    useEffect(() => {
        let active = true
        setLoading(true)
        setError(null)

        firebase.app().functions('europe-west1')
            .httpsCallable('getMaterialOutcomesBreakdown')({ client })
            .then((result) => { if (active) setData(result?.data || null) })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('useMaterialOutcomesBreakdown:', err)
                if (active) setError(err)
            })
            .finally(() => { if (active) setLoading(false) })

        return () => { active = false }
    }, [])

    return { data, loading, error }
}

export default useMaterialOutcomesBreakdown
